import { useEffect, useState } from 'react';

import { format } from 'date-fns';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import type { User } from '@/models/Users';
import type { Subscriptions } from '@/models/Subscription';

const ClientsAdminComponent = () => {
	const [selectedStatus, setSelectedStatus] = useState('ALL');
	const [searchQuery, setSearchQuery] = useState('');

	// Clients
	const [clients, setClients] = useState<User[]>([]);
	useEffect(() => {
		const fetchClients = async () => {
			try {
				const response = await fetch('/api/clients');
				const data = await response.json();
				setClients(data);
			} catch (error) {
				console.error('Failed to fetch clients:', error);
			}
		};
		fetchClients();
	}, []);
	const filteredClients = clients.filter((c) => {
		const matchStatus = selectedStatus === 'ALL' || c.status === selectedStatus;
		const matchSearch = c.email.includes(searchQuery) || c.firstname.includes(searchQuery) || c.lastname.includes(searchQuery);
		return matchStatus && matchSearch;
	});

	// Add Clients
	const [clientRegisterFormData, setClientRegisterFormData] = useState({
		email: '',
		passwrd: 'NA',
		firstname: '',
		lastname: '',
		phone: '',
		address: 'NA',
		status: 'REGISTERED',
		role: 'USER',
		subscription_by: 'NA',
		level: '3 ثانوي',
		branch: 'آداب وفلسفة',
	});
	const [isAddClientDialogOpen, setIsAddClientDialogOpen] = useState(false);
	const openAddClientDialog = () => {
		setClientRegisterFormData({
			email: '',
			passwrd: 'NA',
			firstname: '',
			lastname: '',
			phone: '',
			address: 'NA',
			status: 'REGISTERED',
			role: 'USER',
			subscription_by: 'NA',
			level: '3 ثانوي',
			branch: 'آداب وفلسفة',
		});
		setIsAddClientDialogOpen(true);
	};
	const handleAddClientSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		try {
			fetch('/api/clients', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(clientRegisterFormData),
			});

			const fetchClients = async () => {
				try {
					const response = await fetch('/api/clients');
					const data = await response.json();
					setClients(data);
				} catch (error) {
					console.error('Failed to fetch clients:', error);
				}
			};
			fetchClients();

			setIsAddClientDialogOpen(false);
		} catch (error) {
			console.error('Error: ', error);
		}
	};

	// Delete Client
	const [clientToDeleteId, setClientToDeleteId] = useState(0);
	const [isDeleteClientDialogOpen, setIsDeleteClientDialogOpen] = useState(false);
	const doShowDeleteClientDialog = (id: number) => {
		setClientToDeleteId(id);
		setIsDeleteClientDialogOpen(true);
	};
	const doDeleteClient = () => {
		try {
			fetch('/api/clients/' + clientToDeleteId, {
				method: 'DELETE',
			});

			const fetchClients = async () => {
				try {
					const response = await fetch('/api/clients');
					const data = await response.json();
					setClients(data);
				} catch (error) {
					console.error('Failed to fetch clients:', error);
				}
			};
			fetchClients();

			setClientToDeleteId(0);
			setIsDeleteClientDialogOpen(false);
		} catch (error) {
			console.error('Error: ', error);
		}
	};

	// Change Client status
	const [clientToChangeStatusId, setClientToChangeStatusId] = useState(0);
	const [clientToChangeStatusCode, setClientToChangeStatusCode] = useState('');
	const [clientToChangeStatusMessage, setClientToChangeStatusMessage] = useState('');
	const [isClientChangeStatusDialogOpen, setIsClientChangeStatusDialogOpen] = useState(false);
	const showClientChangeStatusDialog = (id: number, status: string, message: string) => {
		setClientToChangeStatusId(id);
		setClientToChangeStatusCode(status);
		setClientToChangeStatusMessage(message);
		setIsClientChangeStatusDialogOpen(true);
	};
	const doChangeClientStatus = () => {
		try {
			const url = '/api/clients/' + clientToChangeStatusId + '/status/' + clientToChangeStatusCode;
			console.log(url);
			fetch(url, { method: 'PUT' });

			const fetchClients = async () => {
				try {
					const response = await fetch('/api/clients');
					const data = await response.json();
					setClients(data);
				} catch (error) {
					console.error('Failed to fetch clients:', error);
				}
			};
			fetchClients();

			setClientToChangeStatusId(0);
			setClientToChangeStatusCode('');
			setClientToChangeStatusMessage('');
			setIsClientChangeStatusDialogOpen(false);
		} catch (error) {
			console.error('Error: ', error);
		}
	};

	// Change Client password
	const [clientToChangePasswordId, setClientToChangePasswordId] = useState(0);
	const [clientFirstname, setClientFirstname] = useState('');
	const [clientLastname, setClientLastname] = useState('');
	const [password, setPassword] = useState('');
	const [passwordConfirm, setPasswordConfirm] = useState('');
	const [isClientChangePasswordDialogOpen, setIsClientChangePasswordDialogOpen] = useState(false);
	const showClientChangePasswordDialog = (id: number, clientFirstname: string, clientLastname: string) => {
		setClientToChangePasswordId(id);
		setClientFirstname(clientFirstname);
		setClientLastname(clientLastname);
		setPassword('');
		setPasswordConfirm('');
		setIsClientChangePasswordDialogOpen(true);
	};
	const doChangeClientPassword = () => {
		try {
			const url = '/api/clients/' + clientToChangePasswordId + '/pw/' + password;
			fetch(url, { method: 'PUT' });

			const fetchClients = async () => {
				try {
					const response = await fetch('/api/clients');
					const data = await response.json();
					setClients(data);
				} catch (error) {
					console.error('Failed to fetch clients:', error);
				}
			};
			fetchClients();

			setClientToChangePasswordId(0);
			setClientFirstname('');
			setClientLastname('');
			setIsClientChangePasswordDialogOpen(false);
		} catch (error) {
			console.error('Error: ', error);
		}
	};

	// Client details - Subscriptions
	const [clientDetails, setClientDetails] = useState<User>({
		id: 0,
		create_at: new Date(),
		email: '',
		passwrd: '',
		firstname: '',
		lastname: '',
		phone: '',
		address: '',
		status: '',
		role: '',
		subscription_by: '',
		link_id: '',
		subscription_count: 0,
	});

	// Subscriptions
	const [subscriptions, setSubscriptions] = useState<Subscriptions[]>([]);
	const fetchSubscriptions = async (email: string) => {
		try {
			const response = await fetch('/api/subscriptions/user/' + email);
			const data = await response.json();
			setSubscriptions(data);
		} catch (error) {
			console.error('Failed to fetch Subscriptions:', error);
		}
	};

	// Invitations
	const [invitations, setInvitations] = useState<User[]>([]);
	const fetchInvitations = async (email: string) => {
		try {
			const response = await fetch('/api/clients/subscriptions_by/' + email);
			const data = await response.json();
			setInvitations(data);
		} catch (error) {
			console.error('Failed to fetch Invitations:', error);
		}
	};

	const [isClientDetailsDialogOpen, setClientDetailsDialogOpen] = useState(false);
	const showClientDetailsDialog = (user: User) => {
		setClientDetails(user);
		fetchSubscriptions(user.email);
		fetchInvitations(user.email);
		setClientDetailsDialogOpen(true);
	};

	// Add Client subscription
	const [clientToAddSubscriptionId, setClientToAddSubscriptionId] = useState(0);
	const [isClientAddSubscriptionDialogOpen, setIsClientAddSubscriptionDialogOpen] = useState(false);
	const showClientAddSubscriptionDialog = (id: number, clientFirstname: string, clientLastname: string) => {
		setClientToChangePasswordId(id);
		setClientFirstname(clientFirstname);
		setClientLastname(clientLastname);
		setIsClientAddSubscriptionDialogOpen(true);
	};
	const doAddSubscription = () => {
		try {
			const url = '/api/clients/' + clientToChangePasswordId + '/pw/' + password;
			fetch(url, { method: 'PUT' });

			const fetchClients = async () => {
				try {
					const response = await fetch('/api/clients');
					const data = await response.json();
					setClients(data);
				} catch (error) {
					console.error('Failed to fetch clients:', error);
				}
			};
			fetchClients();

			setClientToChangePasswordId(0);
			setClientFirstname('');
			setClientLastname('');
			setIsClientChangePasswordDialogOpen(false);
		} catch (error) {
			console.error('Error: ', error);
		}
	};

	return (
		<div className="min-h-screen" style={{ backgroundColor: '#e6e6b8' }}>
			<div className="top-0 z-10000 w-full px-0 py-3 sm:p-6">
				<Navbar />
			</div>

			<div className="container mx-auto py-6 sm:py-8">
				<div className="text-center font-bold text-2xl sm:text-3xl">إدارة الزبائن و الإشتراكات</div>
			</div>

			<div className="container mx-auto py-6 sm:py-8">
				{/* Filters */}
				<div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center">
					<div className="relative flex-1">
						<Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="ابحث عن زبون..."
							className="pr-10"
							style={{ borderColor: 'green' }}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</div>
					<div className="flex gap-4 flex-wrap">
						<Button
							key="REGISTERED"
							size="sm"
							variant={selectedStatus === 'REGISTERED' ? 'default' : 'outline'}
							onClick={() => setSelectedStatus('REGISTERED')}>
							مسجل
						</Button>
						<Button
							key="ACTIVE"
							size="sm"
							variant={selectedStatus === 'ACTIVE' ? 'default' : 'outline'}
							onClick={() => setSelectedStatus('ACTIVE')}>
							مفعل
						</Button>
						<Button
							key="DEACTIVATE"
							size="sm"
							variant={selectedStatus === 'DEACTIVATE' ? 'default' : 'outline'}
							onClick={() => setSelectedStatus('DEACTIVATE')}>
							مغلق مؤقت
						</Button>
						<Button
							key="NOT_SUBSCRIBED"
							size="sm"
							variant={selectedStatus === 'NOT_SUBSCRIBED' ? 'default' : 'outline'}
							onClick={() => setSelectedStatus('NOT_SUBSCRIBED')}>
							غير مشترك
						</Button>
						<Button key="ALL" size="sm" variant={selectedStatus === 'ALL' ? 'default' : 'outline'} onClick={() => setSelectedStatus('ALL')}>
							الكل
						</Button>
					</div>
				</div>

				{/* Add Client Dialog */}
				<div>
					<Button size="sm" onClick={() => openAddClientDialog()}>
						تسجيل زبون جديد
					</Button>
					<br />
					<br />
				</div>

				{/* Clients Table */}
				{/* Nine columns never fit a phone: keep the cells legible and let the
				    wrapper scroll sideways instead of crushing them. */}
				<div className="overflow-x-auto rounded-sm" style={{ borderWidth: 1, borderColor: 'green' }}>
					<Table dir="rtl" className="min-w-[900px]">
						<TableHeader>
							<TableRow>
								<TableHead className="font-bold text-foreground text-center">الرقم</TableHead>
								<TableHead className="font-bold text-foreground text-center">تاريخ التسجيل</TableHead>
								<TableHead className="font-bold text-foreground text-center">الإسم واللقب</TableHead>
								<TableHead className="font-bold text-foreground text-center">البريد الإلكتروني</TableHead>
								<TableHead className="font-bold text-foreground text-center">رقم الهاتف</TableHead>
								<TableHead className="font-bold text-foreground text-center">وصل الدفع</TableHead>
								<TableHead className="font-bold text-foreground text-center">مسجل من طرف</TableHead>
								<TableHead className="font-bold text-foreground text-center">عدد التابعون</TableHead>
								<TableHead className="font-bold text-foreground text-center">الوضعية الحالة</TableHead>
								<TableHead className="font-bold text-foreground text-center">#</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredClients.map((client, index) => (
								<TableRow key={client.id}>
									<TableCell className="text-center">{index + 1}</TableCell>
									<TableCell className="text-center">{format(new Date(client.create_at), 'yyyy-MM-dd')}</TableCell>
									<TableCell className="text-center">{client.lastname + ' ' + client.firstname}</TableCell>
									<TableCell className="text-center">{client.email}</TableCell>
									<TableCell className="text-center">{client.phone}</TableCell>
									<TableCell className="text-center">
										{client.receipt_num ? (
											<a
												href={`/api/subscriptions/${client.receipt_num}/receipt`}
												target="_blank"
												rel="noreferrer"
												title="فتح وصل الدفع"
												className="inline-flex flex-col items-center gap-1 font-semibold text-primary hover:underline">
												{/* Receipts are usually a photo, but a PDF is accepted too — that
												    fails to decode as an image, so drop the preview and keep the link. */}
												<img
													src={`/api/subscriptions/${client.receipt_num}/receipt`}
													alt=""
													loading="lazy"
													onError={(e) => (e.currentTarget.style.display = 'none')}
													className="h-14 w-14 rounded border object-cover"
												/>
												عرض
											</a>
										) : (
											<span className="text-muted-foreground">—</span>
										)}
									</TableCell>
									<TableCell className="text-center">{client.subscription_by}</TableCell>
									<TableCell className="text-center">{client.subscription_count}</TableCell>
									<TableCell className="text-center">
										{client.status === 'REGISTERED' && (
											<div className="p-1 rounded-md" style={{ backgroundColor: 'mediumblue', color: 'white' }}>
												مسجل
											</div>
										)}

										{client.status === 'ACTIVE' && (
											<div className="p-1 rounded-md" style={{ backgroundColor: 'yellowgreen', color: 'black' }}>
												مفعل
											</div>
										)}

										{client.status === 'DEACTIVATE' && (
											<div className="p-1 rounded-md" style={{ backgroundColor: 'orangered', color: 'black' }}>
												مغلق مؤقت
											</div>
										)}

										{client.status === 'NOT_SUBSCRIBED' && (
											<div className="p-1 rounded-md" style={{ backgroundColor: 'yellow', color: 'black' }}>
												غير مشترك
											</div>
										)}
									</TableCell>
									<TableCell>
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="outline">...</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent>
												<DropdownMenuItem
													onClick={() =>
														showClientChangeStatusDialog(
															client.id,
															'ACTIVE',
															'تفعيل الحساب الخاص بالسيد : ' + client.firstname + ' ' + client.lastname,
														)
													}
													dir="rtl">
													تفعيل الحساب
												</DropdownMenuItem>
												<DropdownMenuItem
													onClick={() =>
														showClientChangeStatusDialog(
															client.id,
															'DEACTIVATE',
															'غلق مؤقت للحساب الخاص بالسيد : ' + client.firstname + ' ' + client.lastname,
														)
													}
													dir="rtl">
													غلق مؤقت للحساب
												</DropdownMenuItem>

												<DropdownMenuSeparator />

												<DropdownMenuItem onClick={() => showClientDetailsDialog(client)} dir="rtl">
													تفاصيل حول الحساب
												</DropdownMenuItem>

												<DropdownMenuSeparator />

												<DropdownMenuItem
													onClick={() => showClientChangePasswordDialog(client.id, client.firstname, client.lastname)}
													dir="rtl">
													تغيير كلمة المرور
												</DropdownMenuItem>

												<DropdownMenuSeparator />

												<DropdownMenuItem onClick={() => doShowDeleteClientDialog(client.id)} style={{ color: 'red' }} dir="rtl">
													حدف
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
						<TableFooter></TableFooter>
					</Table>
				</div>
			</div>

			{/* Add client dialog */}
			<div dir="rtl">
				<Dialog open={isAddClientDialogOpen} onOpenChange={setIsAddClientDialogOpen}>
					<DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-[800px] overflow-y-auto p-4 sm:p-8" style={{ minHeight: 400 }} dir="rtl">
						<DialogHeader>
							<DialogTitle></DialogTitle>
							<DialogDescription></DialogDescription>
						</DialogHeader>
						<form id="lesson-add-form" onSubmit={handleAddClientSubmit} className="space-y-4">
							<br />
							<div className="flex flex-col gap-4 sm:flex-row">
								<div className="w-full min-w-0 space-y-2">
									<Label htmlFor="firstname" className="font-bold">
										الإسم
									</Label>
									<Input
										id="firstname"
										type="text"
										placeholder=""
										className="text-right"
										style={{ borderColor: 'green' }}
										value={clientRegisterFormData.firstname}
										onChange={(e) => setClientRegisterFormData({ ...clientRegisterFormData, firstname: e.target.value })}
										required
									/>
								</div>
								<div className="w-full min-w-0 space-y-2">
									<Label htmlFor="lastname" className="font-bold">
										اللقب
									</Label>
									<Input
										id="lastname"
										type="text"
										placeholder=""
										className="text-right"
										style={{ borderColor: 'green' }}
										value={clientRegisterFormData.lastname}
										onChange={(e) => setClientRegisterFormData({ ...clientRegisterFormData, lastname: e.target.value })}
										required
									/>
								</div>
							</div>

							<div className="flex flex-col gap-4 sm:flex-row">
								<div className="w-full min-w-0 space-y-2">
									<Label htmlFor="email" className="font-bold">
										البريد الإلكتروني
									</Label>
									<Input
										id="email"
										type="email"
										placeholder=""
										className="text-right"
										style={{ borderColor: 'green' }}
										value={clientRegisterFormData.email}
										onChange={(e) => setClientRegisterFormData({ ...clientRegisterFormData, email: e.target.value })}
										required
									/>
								</div>

								<div className="w-full min-w-0 space-y-2">
									<Label htmlFor="phone" className="font-bold">
										رقم الهاتف
									</Label>
									<Input
										id="phone"
										type="text"
										placeholder=""
										className="text-right"
										style={{ borderColor: 'green' }}
										value={clientRegisterFormData.phone}
										onChange={(e) => setClientRegisterFormData({ ...clientRegisterFormData, phone: e.target.value })}
										required
									/>
								</div>
							</div>

							<div className="flex flex-col gap-4 sm:flex-row">
								<div className="w-full min-w-0 space-y-2">
									<Label className="font-bold">المستوى الدراسي</Label>
									<Select
										value={clientRegisterFormData.level}
										onValueChange={(value) => setClientRegisterFormData({ ...clientRegisterFormData, level: value })}
										dir="rtl">
										<SelectTrigger>
											<SelectValue placeholder="اختر المستوى" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="1 ثانوي">السنة الأولى ثانوي</SelectItem>
											<SelectItem value="2 ثانوي">السنة الثانية ثانوي</SelectItem>
											<SelectItem value="3 ثانوي">السنة الثالثة ثانوي</SelectItem>
										</SelectContent>
									</Select>
								</div>

								<div className="w-full min-w-0 space-y-2">
									<Label className="font-bold">الشعبة</Label>
									<Select
										value={clientRegisterFormData.branch}
										onValueChange={(value) => setClientRegisterFormData({ ...clientRegisterFormData, branch: value })}
										dir="rtl">
										<SelectTrigger>
											<SelectValue placeholder="اختر الشعبة" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="علوم تجريبية">علوم تجريبية</SelectItem>
											<SelectItem value="رياضيات">رياضيات</SelectItem>
											<SelectItem value="تقني رياضي">تقني رياضي</SelectItem>
											<SelectItem value="تسيير واقتصاد">تسيير واقتصاد</SelectItem>
											<SelectItem value="آداب وفلسفة">آداب وفلسفة</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>
							<br />
							<br />
						</form>
						<DialogFooter className="sm:justify-start" dir="rtl">
							<Button type="submit" form="lesson-add-form" className="w-1/2" size="sm">
								تسجيل الزبون
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Change client password dialog */}
			<div dir="rtl">
				<Dialog open={isClientChangePasswordDialogOpen} onOpenChange={setIsClientChangePasswordDialogOpen}>
					<DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-[800px] overflow-y-auto p-4 sm:p-8" style={{ minHeight: 400 }} dir="rtl">
						<DialogHeader>
							<DialogTitle></DialogTitle>
							<DialogDescription></DialogDescription>
						</DialogHeader>
						<br />
						<Label className="font-bold text-xl">تغيير كلمة المرور</Label>

						<div className="flex flex-col gap-4 sm:flex-row">
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="firstname" className="font-bold">
									الإسم
								</Label>
								<Input
									id="firstname"
									type="text"
									placeholder=""
									className="text-right"
									style={{ borderColor: 'green' }}
									value={clientFirstname}
									readOnly
								/>
							</div>
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="lastname" className="font-bold">
									اللقب
								</Label>
								<Input
									id="lastname"
									type="text"
									placeholder=""
									className="text-right"
									style={{ borderColor: 'green' }}
									value={clientLastname}
									readOnly
								/>
							</div>
						</div>

						<div className="flex flex-col gap-4 sm:flex-row">
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="pwd1" className="font-bold">
									كلمة المرور
								</Label>
								<Input
									id="pwd1"
									type="password"
									placeholder=""
									className="text-right"
									style={{ borderColor: 'green' }}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									required
								/>
							</div>

							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="pwd2" className="font-bold">
									تأكيد كلمة المرور
								</Label>
								<Input
									id="pwd2"
									type="password"
									placeholder=""
									className="text-right"
									style={{ borderColor: 'green' }}
									value={passwordConfirm}
									onChange={(e) => setPasswordConfirm(e.target.value)}
									required
								/>
							</div>
						</div>
						<br />

						<DialogFooter className="sm:justify-start" dir="rtl">
							<Button
								onClick={() => doChangeClientPassword()}
								className="w-1/2"
								size="sm"
								disabled={password === '' || passwordConfirm === '' || password !== passwordConfirm}>
								تغيير كلمة المرور
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Client delete alert dialog */}
			<div dir="rtl">
				<AlertDialog open={isDeleteClientDialogOpen} onOpenChange={setIsDeleteClientDialogOpen}>
					<AlertDialogContent dir="rtl">
						<div dir="rtl">
							<AlertDialogHeader dir="rtl">
								<AlertDialogTitle>حدف الزبون من المنصة</AlertDialogTitle>
							</AlertDialogHeader>
						</div>
						<AlertDialogFooter dir="rtl">
							<div className="flex flex-row gap-2">
								<Button size="sm" variant="outline" onClick={() => setIsDeleteClientDialogOpen(false)}>
									الغاء
								</Button>
								<Button size="sm" variant="destructive" onClick={() => doDeleteClient()}>
									أحدف
								</Button>
							</div>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>

			{/* Client change status alert dialog */}
			<div dir="rtl">
				<AlertDialog open={isClientChangeStatusDialogOpen} onOpenChange={setIsClientChangeStatusDialogOpen}>
					<AlertDialogContent dir="rtl">
						<div dir="rtl">
							<AlertDialogHeader dir="rtl">
								<AlertDialogTitle>{clientToChangeStatusMessage}</AlertDialogTitle>
							</AlertDialogHeader>
						</div>
						<AlertDialogFooter dir="rtl">
							<div className="flex flex-row gap-2">
								<Button size="sm" variant="outline" onClick={() => setIsClientChangeStatusDialogOpen(false)}>
									الغاء
								</Button>
								<Button size="sm" variant="default" onClick={() => doChangeClientStatus()}>
									موافق
								</Button>
							</div>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>

			{/* Add client dialog */}
			<div dir="rtl">
				<Dialog open={isAddClientDialogOpen} onOpenChange={setIsAddClientDialogOpen}>
					<DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-[800px] overflow-y-auto p-4 sm:p-8" style={{ minHeight: 400 }} dir="rtl">
						<DialogHeader>
							<DialogTitle></DialogTitle>
							<DialogDescription></DialogDescription>
						</DialogHeader>
						<form id="lesson-add-form" onSubmit={handleAddClientSubmit} className="space-y-4">
							<br />
							<div className="flex flex-col gap-4 sm:flex-row">
								<div className="w-full min-w-0 space-y-2">
									<Label htmlFor="firstname" className="font-bold">
										الإسم
									</Label>
									<Input
										id="firstname"
										type="text"
										placeholder=""
										className="text-right"
										style={{ borderColor: 'green' }}
										value={clientRegisterFormData.firstname}
										onChange={(e) => setClientRegisterFormData({ ...clientRegisterFormData, firstname: e.target.value })}
										required
									/>
								</div>
								<div className="w-full min-w-0 space-y-2">
									<Label htmlFor="lastname" className="font-bold">
										اللقب
									</Label>
									<Input
										id="lastname"
										type="text"
										placeholder=""
										className="text-right"
										style={{ borderColor: 'green' }}
										value={clientRegisterFormData.lastname}
										onChange={(e) => setClientRegisterFormData({ ...clientRegisterFormData, lastname: e.target.value })}
										required
									/>
								</div>
							</div>

							<div className="flex flex-col gap-4 sm:flex-row">
								<div className="w-full min-w-0 space-y-2">
									<Label htmlFor="email" className="font-bold">
										البريد الإلكتروني
									</Label>
									<Input
										id="email"
										type="email"
										placeholder=""
										className="text-right"
										style={{ borderColor: 'green' }}
										value={clientRegisterFormData.email}
										onChange={(e) => setClientRegisterFormData({ ...clientRegisterFormData, email: e.target.value })}
										required
									/>
								</div>

								<div className="w-full min-w-0 space-y-2">
									<Label htmlFor="phone" className="font-bold">
										رقم الهاتف
									</Label>
									<Input
										id="phone"
										type="text"
										placeholder=""
										className="text-right"
										style={{ borderColor: 'green' }}
										value={clientRegisterFormData.phone}
										onChange={(e) => setClientRegisterFormData({ ...clientRegisterFormData, phone: e.target.value })}
										required
									/>
								</div>
							</div>

							<div className="flex flex-col gap-4 sm:flex-row">
								<div className="w-full min-w-0 space-y-2">
									<Label className="font-bold">المستوى الدراسي</Label>
									<Select
										value={clientRegisterFormData.level}
										onValueChange={(value) => setClientRegisterFormData({ ...clientRegisterFormData, level: value })}
										dir="rtl">
										<SelectTrigger>
											<SelectValue placeholder="اختر المستوى" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="1 ثانوي">السنة الأولى ثانوي</SelectItem>
											<SelectItem value="2 ثانوي">السنة الثانية ثانوي</SelectItem>
											<SelectItem value="3 ثانوي">السنة الثالثة ثانوي</SelectItem>
										</SelectContent>
									</Select>
								</div>

								<div className="w-full min-w-0 space-y-2">
									<Label className="font-bold">الشعبة</Label>
									<Select
										value={clientRegisterFormData.branch}
										onValueChange={(value) => setClientRegisterFormData({ ...clientRegisterFormData, branch: value })}
										dir="rtl">
										<SelectTrigger>
											<SelectValue placeholder="اختر الشعبة" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="علوم تجريبية">علوم تجريبية</SelectItem>
											<SelectItem value="رياضيات">رياضيات</SelectItem>
											<SelectItem value="تقني رياضي">تقني رياضي</SelectItem>
											<SelectItem value="تسيير واقتصاد">تسيير واقتصاد</SelectItem>
											<SelectItem value="آداب وفلسفة">آداب وفلسفة</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>
							<br />
							<br />
						</form>
						<DialogFooter className="sm:justify-start" dir="rtl">
							<Button type="submit" form="lesson-add-form" className="w-1/2" size="sm">
								تسجيل الزبون
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Client details */}
			<div dir="rtl">
				<Dialog open={isClientDetailsDialogOpen} onOpenChange={setClientDetailsDialogOpen}>
					<DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-[800px] overflow-y-auto p-4 sm:p-8" style={{ minHeight: 400 }} dir="rtl">
						<DialogHeader dir="rtl">
							<DialogTitle>تفاصيل حول الزبون</DialogTitle>
							<DialogDescription />
						</DialogHeader>

						<div className="flex flex-col gap-4 sm:flex-row">
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="firstname" className="font-bold">
									الإسم
								</Label>
								<Input
									id="firstname"
									type="text"
									placeholder=""
									className="text-right"
									style={{ borderColor: 'green' }}
									value={clientDetails?.firstname}
									readOnly
								/>
							</div>
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="lastname" className="font-bold">
									اللقب
								</Label>
								<Input
									id="lastname"
									type="text"
									placeholder=""
									className="text-right"
									style={{ borderColor: 'green' }}
									value={clientDetails?.lastname}
									readOnly
								/>
							</div>
						</div>

						<div className="flex flex-col gap-4 sm:flex-row">
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="email" className="font-bold">
									البريد الإلكتروني
								</Label>
								<Input
									id="email"
									type="text"
									placeholder=""
									className="text-right"
									style={{ borderColor: 'green' }}
									value={clientDetails?.email}
									readOnly
								/>
							</div>
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="phone" className="font-bold">
									رقم الهاتف
								</Label>
								<Input
									id="phone"
									type="text"
									placeholder=""
									className="text-right"
									style={{ borderColor: 'green' }}
									value={clientDetails?.phone}
									readOnly
								/>
							</div>
						</div>

						<div className="flex flex-col gap-4 sm:flex-row">
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="createDate" className="font-bold">
									تاريخ التسجيل
								</Label>
								<Input
									id="createDate"
									type="text"
									placeholder=""
									className="text-right"
									style={{ borderColor: 'green' }}
									value={format(new Date(clientDetails?.create_at!), 'yyyy-MM-dd')}
									readOnly
								/>
							</div>
							<div className="w-full min-w-0 space-y-2">
								<Label htmlFor="lastname" className="font-bold">
									مسجل من طرف
								</Label>
								<Input
									id="lastname"
									type="text"
									placeholder=""
									className="text-right"
									style={{ borderColor: 'green' }}
									value={clientDetails?.subscription_by}
									readOnly
								/>
							</div>
						</div>

						<div className="min-w-0" dir="rtl">
							<Tabs defaultValue="invitations" className="w-full" dir="rtl">
								<TabsList className="h-auto w-full flex-wrap justify-start">
									<TabsTrigger value="invitations">المسجلون عن طريق الدعوة</TabsTrigger>
									<TabsTrigger value="subscriptions">الاشتراكات</TabsTrigger>
								</TabsList>
								<TabsContent value="invitations">
									<div className="overflow-x-auto" dir="rtl">
										<Table dir="rtl" className="min-w-[620px]">
											<TableHeader>
												<TableRow>
													<TableHead className="font-bold text-foreground text-center">الرقم</TableHead>
													<TableHead className="font-bold text-foreground text-center">تاريخ التسجيل</TableHead>
													<TableHead className="font-bold text-foreground text-center">الإسم واللقب</TableHead>
													<TableHead className="font-bold text-foreground text-center">البريد الإلكتروني</TableHead>
													<TableHead className="font-bold text-foreground text-center">رقم الهاتف</TableHead>
													<TableHead className="font-bold text-foreground text-center">عدد التابعون</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{invitations?.map((invitation, index) => (
													<TableRow key={invitation.id}>
														<TableCell className="text-center">{index + 1}</TableCell>
														<TableCell className="text-center">{format(new Date(invitation.create_at), 'yyyy-MM-dd')}</TableCell>
														<TableCell className="text-center">{invitation.lastname + ' ' + invitation.firstname}</TableCell>
														<TableCell className="text-center">{invitation.email}</TableCell>
														<TableCell className="text-center">{invitation.phone}</TableCell>
														<TableCell className="text-center">{invitation.subscription_count}</TableCell>
													</TableRow>
												))}
											</TableBody>
											<TableFooter></TableFooter>
										</Table>
									</div>
								</TabsContent>
								<TabsContent value="subscriptions">
									<div className="overflow-x-auto" dir="rtl">
										<Table dir="rtl" className="min-w-[320px]">
											<TableHeader>
												<TableRow>
													<TableHead className="font-bold text-foreground text-center">الرقم</TableHead>
													<TableHead className="font-bold text-foreground text-center">تاريخ التسجيل</TableHead>
													<TableHead className="font-bold text-foreground text-center">المبلغ</TableHead>
													<TableHead className="font-bold text-foreground text-center">وصل الدفع</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{subscriptions?.map((subscription, index) => (
													<TableRow key={subscription.id}>
														<TableCell className="text-center">{index + 1}</TableCell>
														<TableCell className="text-center">{format(new Date(subscription.create_at), 'yyyy-MM-dd')}</TableCell>
														<TableCell className="text-center">{subscription.amount}</TableCell>
														<TableCell className="text-center">
															<a
																href={`/api/subscriptions/${subscription.num}/receipt`}
																target="_blank"
																rel="noreferrer"
																className="font-semibold text-primary hover:underline">
																عرض
															</a>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
											<TableFooter></TableFooter>
										</Table>
									</div>
								</TabsContent>
							</Tabs>
						</div>

						<DialogFooter className="sm:justify-start" dir="rtl"></DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			<Footer />
		</div>
	);
};

export default ClientsAdminComponent;
